import Image from 'next/image';
import { Trans, useTranslation } from 'next-i18next';
import { useRef } from 'react';

import Button from '@/common/button';
import Header from '@/common/header';
import { LinksMirrorDownload } from '@/common/link-mirror-download';
import Sponsors from '@/components/sponsors';
import { useTrackSoftwareDownload } from '@/hooks/use-track-software-download';

import { Faq } from '../faq';
import { Games } from '../games';
import { Influencers } from '../influencers';
import { SignUpCustomTrial } from '../sign-up-custom-trial';
import type { DefaultTrialProps } from '../trial-layout-switcher';

const CreditCardIcon = () => (
  <svg width='25' height='17' viewBox='0 0 25 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fill-rule='evenodd'
      clip-rule='evenodd'
      d='M2.09322 0.051032C1.96387 0.0753814 1.68737 0.177677 1.4788 0.278325C0.952675 0.532228 0.535536 0.949088 0.285431 1.47093C0.0455805 1.97125 0 2.24813 0 3.20417C0 3.85835 0.0126064 4.00544 0.0738978 4.06685C0.14074 4.13373 1.29182 4.14084 12.0419 4.14084C22.792 4.14084 23.9431 4.13373 24.0099 4.06685C24.0712 4.00544 24.0838 3.85835 24.0838 3.20417C24.0838 2.24799 24.0382 1.97129 23.7982 1.47051C23.4541 0.752597 22.7166 0.185448 21.9467 0.0466047C21.5866 -0.0183428 2.43919 -0.0140569 2.09322 0.051032ZM0.0738978 5.72196C0.00780842 5.78813 0 6.23876 0 9.97567C0 12.7359 0.017075 14.2502 0.0502844 14.4348C0.134295 14.9018 0.424618 15.4403 0.784935 15.7975C1.14539 16.1549 1.67886 16.4416 2.13716 16.5243C2.52814 16.5948 21.5557 16.5948 21.9467 16.5243C22.4131 16.4401 22.9509 16.1495 23.3077 15.7887C23.6647 15.4278 23.951 14.8936 24.0335 14.4348C24.0667 14.2502 24.0838 12.7359 24.0838 9.97567C24.0838 6.23876 24.076 5.78813 24.0099 5.72196C23.9431 5.65508 22.792 5.64797 12.0419 5.64797C1.29182 5.64797 0.14074 5.65508 0.0738978 5.72196ZM5.67376 10.6668C5.9742 10.8917 5.99743 10.9642 5.99743 11.6765C5.99743 12.262 5.98746 12.3423 5.90044 12.459C5.67602 12.7599 5.60348 12.7832 4.89202 12.7832C4.30724 12.7832 4.22704 12.7733 4.11048 12.6861C3.8282 12.4751 3.78793 12.3712 3.77057 11.8095C3.75096 11.1742 3.7781 11.0153 3.93902 10.8238C4.1434 10.5807 4.28015 10.5446 4.94758 10.5578C5.46552 10.5681 5.5611 10.5824 5.67376 10.6668Z'
      fill='#93FF18'
    />
  </svg>
);

const FullAccessIcon = () => (
  <svg width='25' height='24' viewBox='0 0 25 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fill-rule='evenodd'
      clip-rule='evenodd'
      d='M11.6926 0.0519046C11.3716 0.15801 11.1965 0.265901 11.0197 0.466553C10.9199 0.579851 10.2512 1.87241 9.34394 3.70581C8.51362 5.3837 7.82594 6.76506 7.81571 6.77552C7.80553 6.78594 6.26111 7.01507 4.38368 7.28462C0.546579 7.83558 0.657235 7.80835 0.286179 8.29428C0.0619369 8.58794 -0.0222471 8.87357 0.00492027 9.24853C0.0448022 9.7996 0.0562753 9.81294 2.72697 12.4116C4.47428 14.1118 5.19543 14.8445 5.18117 14.9051C5.06724 15.3889 4.06885 21.4175 4.06885 21.6217C4.06885 21.771 4.1063 22.003 4.15209 22.1372C4.26394 22.4651 4.6563 22.8569 4.98454 22.9686C5.26779 23.065 5.71995 23.077 5.9573 22.9943C6.048 22.9627 7.33087 22.3016 8.80809 21.525C10.2853 20.7484 11.6284 20.0466 11.7927 19.9653L12.0915 19.8176L15.0675 21.3947C16.7042 22.2622 18.1566 23.0018 18.295 23.0383C19.0841 23.2468 19.8951 22.7358 20.0639 21.9235C20.1171 21.6676 20.0846 21.4418 19.536 18.2542L18.9515 14.8576L20.1148 13.7088C20.7547 13.077 21.864 11.9829 22.58 11.2776C24.0728 9.80704 24.1099 9.75505 24.1077 9.13757C24.1065 8.80621 24.0844 8.69906 23.9743 8.48824C23.8226 8.19795 23.557 7.94675 23.2673 7.81961C23.1363 7.76212 21.8865 7.5629 19.6842 7.24841L16.3023 6.76545L14.8169 3.74391C13.7054 1.48291 13.2788 0.662543 13.1219 0.484659C12.7738 0.0898028 12.1496 -0.0991428 11.6926 0.0519046Z'
      fill='#93FF18'
    />
  </svg>
);

const PCandMobileIcon = () => (
  <svg width='24' height='21' viewBox='0 0 24 21' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fill-rule='evenodd'
      clip-rule='evenodd'
      d='M4.20607 0.0297955C3.54958 0.12727 3.01133 0.597977 2.82015 1.24175C2.75834 1.44997 2.74642 1.78568 2.74642 3.32037V5.15059H3.53776H4.3291V3.3867C4.3291 1.8212 4.33738 1.61964 4.40274 1.59455C4.44323 1.579 8.03419 1.5663 12.3826 1.5663C19.508 1.5663 20.2961 1.57351 20.362 1.63942C20.4277 1.70511 20.4352 2.2644 20.4352 7.09966C20.4352 11.2276 20.4221 12.4998 20.3793 12.5426C20.3367 12.5852 19.3532 12.5985 16.2364 12.5985H12.1494V13.3915V14.1844L16.397 14.1712L20.6446 14.1579L20.9122 14.0498C21.253 13.9122 21.637 13.562 21.7972 13.2428C21.8659 13.1061 21.9442 12.8475 21.9714 12.6683C22.0049 12.4484 22.0166 10.5641 22.0077 6.87292L21.9946 1.40337L21.8883 1.1406C21.6799 0.625161 21.3763 0.321567 20.8609 0.113119L20.5981 0.00689323L12.5218 0.000562447C8.07981 -0.00292875 4.33775 0.0101983 4.20607 0.0297955ZM1.49778 6.78308C0.786226 6.9231 0.203848 7.49943 0.0523296 8.21349C0.0153695 8.38787 8.19433e-06 10.0119 8.19433e-06 13.7547C8.19433e-06 19.4305 -0.00497258 19.3011 0.23066 19.7629C0.463081 20.2186 1.06119 20.6168 1.63827 20.7003C2.04241 20.7587 8.54833 20.7551 8.96075 20.6963C9.6043 20.6044 10.1162 20.2269 10.3948 19.6387L10.5434 19.3249L10.5556 13.8162C10.5689 7.83165 10.579 8.09209 10.3149 7.64601C10.0636 7.22143 9.5665 6.87264 9.08178 6.7807C8.77572 6.72266 1.79369 6.72485 1.49778 6.78308ZM8.89889 8.39299C8.98035 8.46667 8.98403 8.68006 8.98403 13.3039V18.1379H5.28336H1.58269V13.312C1.58269 8.88688 1.58906 8.47915 1.65972 8.40109C1.73309 8.32004 1.90634 8.31595 5.27526 8.31595C8.50257 8.31595 8.82125 8.32274 8.89889 8.39299ZM12.1494 15.95V17.0672L17.1651 17.0669C20.1123 17.0668 22.2922 17.0484 22.4509 17.0224C22.8043 16.9646 23.1025 16.8124 23.3536 16.5617C23.5873 16.3284 23.6828 16.1614 23.7701 15.8337C23.8577 15.5049 23.8483 15.3103 23.7357 15.1282C23.5406 14.8125 23.9522 14.8329 17.7609 14.8329H12.1494V15.95ZM5.5903 18.8151C6.0112 19.0327 6.0112 19.7101 5.5903 19.9277C5.03609 20.2143 4.44589 19.6202 4.73999 19.0717C4.89653 18.7799 5.2916 18.6606 5.5903 18.8151Z'
      fill='#93FF18'
    />
  </svg>
);

const WindowsIcon = () => (
  <svg width='50' height='50' viewBox='0 0 50 50' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M0 7.07917L20.4334 4.30504L20.4426 23.954L0.0188949 24.0699L0 7.07917ZM20.4237 26.2178L20.4397 45.8839L0.0160321 43.0847L0.0148869 26.086L20.4237 26.2178ZM22.9007 3.94201L49.9937 0V23.704L22.9007 23.9186V3.94201ZM50 26.4028L49.9937 50L22.9007 46.1881L22.8629 26.3588L50 26.4028Z' fill='black' />
  </svg>
);

interface GoToTheTrialProps extends DefaultTrialProps {}

export function GoToTheTrial({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: GoToTheTrialProps) {
  const { t } = useTranslation('components/trial/lp/go-to-the-trial');
  const urlDownload = LinksMirrorDownload();
  const trackDownload = useTrackSoftwareDownload('trial_go_to_the_trial');
  const scrollToFormRef = useRef<HTMLDivElement>(null);

  const handleScrollToForm = () => {
    const targetElement = scrollToFormRef.current;
    const elementOffSetTop = targetElement?.getBoundingClientRect().top || 0;
    const scrollPosition = elementOffSetTop + window.scrollY - 100;
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  };

  function getMainCompetitorsColumn(i: number) {
    const bgClass = i % 2 === 0 ? 'bg-[#060607]' : 'bg-[#0A0A0C]';
    if (i === 0) {
      return (
        <div className={`flex h-[88px] flex-col items-center justify-center p-2.5 xl:h-[80px] ${bgClass}`}>
          <span className='text-center text-[16px] font-medium text-white'>{t('comparison.main_competitors')}</span>
        </div>
      );
    }
    if (i > 4 && i < 8) {
      return (
        <div className={`flex h-[88px] flex-col items-center justify-center p-2.5 xl:h-[80px] ${bgClass}`}>
          <Image src='/assets/images/competitors/yes.webp' alt={t('comparison.yes')} width={29} height={29} />
        </div>
      );
    }
    if (i === 8) {
      return (
        <div className={`flex h-[88px] flex-col items-center justify-center p-2.5 xl:h-[80px] ${bgClass}`}>
          <Image src='/assets/images/competitors/attention.webp' alt='' width={29} height={29} />
          <div className='text-center text-[16px] font-medium text-white'>{t('comparison.less_options')}</div>
        </div>
      );
    }
    return (
      <div className={`flex h-[88px] flex-col items-center justify-center p-2.5 xl:h-[80px] ${bgClass}`}>
        <Image src='/assets/images/competitors/no.webp' alt={t('comparison.no')} width={29} height={29} />
      </div>
    );
  }

  function getYesLogoColumn(i: number) {
    const bgClass = i % 2 === 0 ? 'bg-[#060607]' : 'bg-[#0A0A0C]';
    return <div className={`flex h-[88px] flex-col items-center justify-center p-2.5 xl:h-[80px] ${bgClass}`}>{i === 0 ? <Image src='/assets/images/competitors/logo.webp' alt='NoPing' width={119} height={26} /> : <Image src='/assets/images/competitors/yes.webp' alt={t('comparison.yes')} width={29} height={29} />}</div>;
  }

  function getFeatureColumn(i: number) {
    if (i === 0) {
      return <div className='flex h-[88px] items-center bg-[#060607] p-2.5 text-[16px] font-bold text-white tablet:p-5 xl:h-[80px] xl:text-[36px]'>{t(`comparison.features.${i}`)}</div>;
    }
    const bgClass = i % 2 === 0 ? 'bg-[#060607]' : 'bg-[#0A0A0C]';
    return <div className={`h-[88px] p-2.5 text-[16px] font-medium text-white tablet:p-5 xl:h-[80px] ${bgClass}`}>{t(`comparison.features.${i}`)}</div>;
  }

  function handleDownloadClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    // Tracking must not hold the installer navigation.
    setTimeout(() => {
      window.location.href = urlDownload;
    }, 300);
    trackDownload(urlDownload);

    fetch('/api/ga4-download', {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        events: [
          {
            name: 'download_click',
            params: {
              idioma_navegador: navigator?.language || 'NP_UNKNOWN_NP',
              pais: 'NP_UNKNOWN_NP', // será sobrescrito no backend
              origem: sessionStorage.getItem('idref') || 'NP_UNKNOWN_NP',
              ip_do_usuario_no_site: 'NP_UNKNOWN_NP', // será sobrescrito no backend
            },
          },
        ],
      }),
    }).catch(() => {});
  }

  return (
    <div className='bg-[#161616]'>
      <Header variant='primary' />
      <section className="bg-[url('/assets/images/trial/ping-proof/bg-hero.webp')] bg-cover bg-center">
        <div className='mx-auto flex max-w-[1400px] flex-col items-center justify-center gap-4 px-5 py-20 tablet:py-32 ipad:flex-row'>
          <div className='flex w-full flex-col gap-4'>
            <Image src='/assets/images/trial/new/logo-np.webp' alt='Logo NoPing' width={150} height={35} />

            <h1 className='text-6xl font-bold leading-10 text-white'>
              <Trans i18nKey='first_section.title' components={{ span: <span className='text-[#93FF18]' /> }}>
                {t('first_section.title')}
              </Trans>
            </h1>
            <p className='text-xl font-normal text-white'>{t('first_section.subtitle')}</p>

            <div className='flex items-center justify-start gap-4'>
              <div className='flex items-center gap-1.5 text-base font-bold uppercase text-white'>
                <CreditCardIcon />
                <span>{t('first_section.no_credit_card')}</span>
              </div>

              <div className='flex items-center gap-1.5 text-base font-bold uppercase text-white'>
                <FullAccessIcon />
                <span>{t('first_section.full_access')}</span>
              </div>

              <div className='flex items-center gap-1.5 text-base font-bold uppercase text-white'>
                <PCandMobileIcon />
                <span>PC + Mobile</span>
              </div>
            </div>

            <div className='flex flex-col items-center justify-center gap-2 tablet:flex-row tablet:items-start tablet:justify-start'>
              <Image src='/assets/images/trial/new/pc-client.webp' alt='PC Client image' width={508} height={307} />
              <Image src='/assets/images/trial/new/mobile-client.webp' alt='Mobile Client image' width={151} height={306} />
            </div>
          </div>
          <div ref={scrollToFormRef} className='w-full'>
            <SignUpCustomTrial daysTrial={daysTrial} trialConfigId={trialConfigId} trialPageId={trialPageId} isDisabledButton={isDisabledButton} />
          </div>
        </div>
      </section>

      <section className='mx-auto mt-10 flex max-w-[1400px] flex-col items-stretch gap-4 px-2.5 ipad:flex-row'>
        <div className='flex w-full flex-col items-center justify-center gap-5 overflow-hidden rounded-[12px] xl:gap-[40px]'>
          <div className='flex w-full'>
            <div className='flex w-full flex-col border-e-2 border-[#40444A]'>
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i}>{getFeatureColumn(i)}</div>
              ))}
            </div>
            <div className='flex w-full max-w-[180px] flex-col border-e-2 border-[#40444A]'>
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i}>{getYesLogoColumn(i)}</div>
              ))}
            </div>
            <div className='flex w-full max-w-[180px] flex-col'>
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i}>{getMainCompetitorsColumn(i)}</div>
              ))}
            </div>
          </div>
        </div>

        <div className='w-full'>
          <h3 className='text-2xl font-bold text-white'>
            <Trans i18nKey='fifth_section.text01' components={{ span: <span className='text-[#93FF18]' /> }}>
              {t('fifth_section.text01')}
            </Trans>
          </h3>
          <div className='mb-4 mt-5 grid gap-4 font-bold text-white tablet:grid-cols-2'>
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className='flex items-center justify-start gap-2 rounded-full bg-[#1D2028E5] p-4'>
                <Image alt='' src={`/assets/images/trial/icons/${i + 1}.webp`} width={55.74} height={55.74} className='object-cover' />
                <p className='uppercase'>
                  <Trans i18nKey={`hero.feature_${i + 1}`} components={{ span: <span className='text-[#93FF18]' /> }}>
                    {t(`hero.feature_${i + 1}`)}
                  </Trans>
                </p>
              </div>
            ))}
          </div>

          <div className='flex flex-col gap-4'>
            <Button variant='primary' fullWidth onClick={handleScrollToForm} disabled={isDisabledButton}>
              <span>{t('fifth_section.button', { count: daysTrial })}</span>
            </Button>

            <div className='relative mb-auto w-full rounded-xl border border-[#20252E] px-4 py-2 pt-6 tablet:w-auto'>
              <div className='absolute start-4 top-0 flex -translate-y-1/2 items-center gap-2 bg-transparent px-1'>
                <div className='h-2 w-1.5 bg-[#AEF55C]' />
                <p className='font-medium text-white'>{t('hero.certified_by')}</p>
              </div>
              <div className='flex flex-col items-center justify-center gap-10 mobile:flex-row'>
                <Image src='/assets/images/footer/digicert.webp' alt='DigiCert Logo' width={125} height={50} />
                <Image src='/assets/images/footer/microsoft.webp' alt='Microsoft Logo' width={68} height={70} />
                <Image src='/assets/images/footer/google-cloud.webp' alt='Google Cloud Logo' width={200} height={31} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Games isActivedButton={false} daysTrial={daysTrial} />

      <section className='mx-auto flex w-full flex-col items-center justify-center px-2.5'>
        <h3 className='mb-10 text-center text-5xl font-bold leading-10 text-white tablet:text-7xl'>
          <Trans i18nKey='fourth_section.text01' components={{ span: <span className='text-[#93FF18]' /> }}>
            {t('fourth_section.text01')}
          </Trans>
        </h3>
        <Image src='/assets/images/trial/new/servers-map.webp' alt='Mapa de servidores NoPing' width={1204} height={719} className='w-full max-w-[1204px] object-cover' />
      </section>

      <Influencers t={t} />

      <section className='mx-auto mb-10 flex w-full flex-col items-center justify-center px-2.5'>
        <h3 className='mb-10 text-center text-6xl font-bold uppercase leading-10 text-white tablet:text-7xl'>
          <Trans i18nKey='sixth_section.title' components={{ span: <span className='uppercase text-[#93FF18]' /> }}>
            {t('sixth_section.title', { count: daysTrial })}
          </Trans>
        </h3>

        <div className='flex w-full flex-col items-center justify-center bg-[url(/assets/images/trial/new/bg-second-section.webp)] bg-cover bg-center bg-no-repeat'>
          <div className='w-full max-w-[1320px] space-y-4'>
            <Image src='/assets/images/trial/new/client.webp' width={1320} height={482} alt='Imagem de clientes NoPing' />
            <a href={urlDownload} download className='flex items-center justify-center gap-5 rounded-lg bg-white text-4xl hover:opacity-80' onClick={handleDownloadClick}>
              <span className='hidden sm:flex'>
                <WindowsIcon />
              </span>
              <span className='p-2.5 text-2xl font-bold uppercase text-black mobile:px-10 tablet:text-4xl'>{t('sixth_section.button')}</span>
            </a>
          </div>
        </div>
      </section>

      <div className='mb-10 w-full bg-black'>
        <Sponsors withBottomPadding />
      </div>

      <Faq t={t} daysTrial={daysTrial} />

      <footer className='mx-auto flex w-full flex-col items-center justify-center gap-10 px-5 py-4 mobile:py-10'>
        <div className='flex flex-wrap items-center justify-center gap-5 tablet:gap-10'>
          <Image src='/assets/images/trial/new/logo-np.webp' alt='Logo NoPing' width={150} height={35} />
          <p className='text-center text-3xl font-normal leading-10 text-white tablet:text-5xl'>{t('footer.text01', { year: new Date().getFullYear() })}</p>
        </div>
      </footer>
    </div>
  );
}
